import { useCallback, useEffect, useRef, useState } from 'react';
import type { ReportRange } from '@order-pilot/shared';
import { Button } from '@/components/Button';
import { IconChevronRight } from '@/components/Icons';
import { downloadReportExport } from '@/features/portal/api';
import type { ExportFormat, ExportableReport } from '@/features/portal/api';

export interface ExportMenuProps {
  report: ExportableReport;
  range: ReportRange;
  /** Disabled while the report itself is still loading or errored. */
  disabled?: boolean;
}

/** Two choices, named and nothing more. */
const FORMATS: ReadonlyArray<{ id: ExportFormat; label: string }> = [
  { id: 'xlsx', label: 'Excel' },
  { id: 'pdf', label: 'PDF' },
];

/**
 * Export the report currently on screen.
 *
 * The file is generated server-side and fetched WITH the portal's bearer token,
 * then saved from a blob. A plain `<a download>` would arrive unauthenticated —
 * the portal has no session cookie — so it would download the login error page
 * under a .xlsx name, which is worse than failing.
 */
export function ExportMenu({ report, range, disabled = false }: ExportMenuProps) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState<ExportFormat | null>(null);
  const [error, setError] = useState<string | null>(null);
  const wrapper = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return undefined;
    const onPointer = (event: MouseEvent) => {
      if (!wrapper.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const run = useCallback(
    async (format: ExportFormat) => {
      setBusy(format);
      setError(null);
      try {
        await downloadReportExport(report, format, range);
        setOpen(false);
      } catch (cause) {
        setError(cause instanceof Error ? cause.message : 'Export failed.');
      } finally {
        setBusy(null);
      }
    },
    [range, report],
  );

  return (
    <div className="relative" ref={wrapper}>
      <Button
        onClick={() => setOpen((value) => !value)}
        disabled={disabled || busy !== null}
        loading={busy !== null}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        {busy ? 'Preparing…' : 'Export'}
        <IconChevronRight
          className={`h-3.5 w-3.5 transition-transform ${open ? '-rotate-90' : 'rotate-90'}`}
        />
      </Button>

      {open ? (
        <div
          role="menu"
          aria-label="Export format"
          className="absolute right-0 z-40 mt-2 w-40 animate-panel-in overflow-hidden rounded-xl border border-slate-200 bg-white shadow-lg"
        >
          {FORMATS.map((format) => (
            <button
              key={format.id}
              type="button"
              role="menuitem"
              disabled={busy !== null}
              onClick={() => void run(format.id)}
              className="flex w-full items-center px-4 py-2.5 text-left text-sm font-medium text-slate-900 transition-colors hover:bg-slate-100 disabled:opacity-60 focus-visible:bg-slate-100 focus-visible:outline-none"
            >
              {format.label}
            </button>
          ))}
        </div>
      ) : null}

      {error ? (
        <p role="alert" className="absolute right-0 mt-2 w-56 text-xs text-rose-700">
          {error}
        </p>
      ) : null}
    </div>
  );
}
